/**
 * Billing dashboard: tier summary, billing preview, system list with tier badges and upgrade/downgrade.
 * Plan: billing_infrastructure_plan §2, Phase C.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ExternalLink, MoreHorizontal, ArrowRight, Pencil, Check, X } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import type { AccountTier } from '@/contexts/AppContext';
import { useBillingPreview, type TierSummary, type BillingPreview } from '@/hooks/useBillingPreview';
import { useTierChange } from '@/hooks/useTierChange';
import { useConnectAccount } from '@/hooks/useConnectAccount';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const TIER_LABEL: Record<string, string> = {
  monitor: 'Monitor',
  alert: 'Alert',
  predict: 'Predict',
};

type PlantRow = { id: string; display_name: string; property_name: string; tier?: string };

export default function Billing() {
  const { organization, properties, plants, refreshData } = useApp();
  const { canSee, tierSummary, billingPreview, nextThreshold, isLoading, refetch: refetchBillingPreview } = useBillingPreview();
  const { upgradeTier, downgradeTier } = useTierChange();
  const [portalLoading, setPortalLoading] = useState(false);

  // Refetch app data (plants with tier) and billing preview when Billing page mounts so tier changes (e.g. after SQL upgrade) show immediately.
  useEffect(() => {
    void refreshData();
    void refetchBillingPreview();
  }, [refreshData, refetchBillingPreview]);

  // If org has no Stripe linked yet, ensure billing (create Stripe customer + subscription and link). Idempotent; then refresh so UI updates.
  useEffect(() => {
    if (!organization?.id || organization.stripe_customer_id) return;
    let cancelled = false;
    (async () => {
      try {
        await invokeAuthedFunction(supabase, 'billing-ensure', { org_id: organization.id });
        if (!cancelled) void refreshData();
      } catch {
        // Billing setup failed; user can retry or contact support. No toast here to avoid noise on first load.
      }
    })();
    return () => { cancelled = true; };
  }, [organization?.id, organization?.stripe_customer_id, refreshData]);
  const [upgradeModal, setUpgradeModal] = useState<{
    plant: PlantRow;
    fromTier: AccountTier;
    toTier: AccountTier;
    deltaMonthly: number;
  } | null>(null);
  const [downgradeModal, setDowngradeModal] = useState<{
    plant: PlantRow;
    fromTier: AccountTier;
    toTier: AccountTier;
    effectiveAt: string;
  } | null>(null);

  const openStripePortal = async () => {
    if (!organization?.id) return;
    setPortalLoading(true);
    try {
      const data = await invokeAuthedFunction<{ url?: string; error?: string }>(supabase, 'billing-portal', {
        org_id: organization.id,
        return_path: '/app/billing',
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.error) {
        const msg = data.error.includes('No Stripe customer') || data.error.includes('No billing')
          ? 'Billing not set up for this account. Contact support to add payment method and access invoices.'
          : data.error;
        toast.error(msg);
      } else {
        toast.error('Could not open billing portal.');
      }
    } catch (e) {
      const err = e as { context?: { body?: { error?: string } }; message?: string };
      const bodyError = err?.context?.body?.error ?? (typeof err?.message === 'string' ? err.message : null);
      const msg = bodyError && (bodyError.includes('No Stripe customer') || bodyError.includes('No billing'))
        ? 'Billing not set up for this account. Contact support to add payment method and access invoices.'
        : 'Failed to open billing portal.';
      toast.error(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const openUpgradeModal = async (plant: PlantRow, fromTier: AccountTier, toTier: AccountTier) => {
    if (!organization?.id) return;
    const { data } = await supabase.rpc('get_org_billing_preview_scenario', {
      p_org_id: organization.id,
      p_move: { from: fromTier, to: toTier, count: 1 },
    });
    const deltaMonthly = (data as { delta_monthly?: number } | null)?.delta_monthly ?? 0;
    setUpgradeModal({ plant, fromTier, toTier, deltaMonthly });
  };

  const openDowngradeModal = (plant: PlantRow, fromTier: AccountTier, toTier: AccountTier) => {
    const effectiveAt = organization?.subscription_current_period_end ?? '';
    if (!effectiveAt) {
      toast.error('Billing period end not available. Contact support.');
      return;
    }
    setDowngradeModal({ plant, fromTier, toTier, effectiveAt });
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeModal) return;
    try {
      await upgradeTier.mutateAsync({
        plantId: upgradeModal.plant.id,
        fromTier: upgradeModal.fromTier,
        toTier: upgradeModal.toTier,
      });
      setUpgradeModal(null);
      toast.success('Tier upgraded. Billing updated.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upgrade failed.');
    }
  };

  const handleConfirmDowngrade = async () => {
    if (!downgradeModal) return;
    try {
      await downgradeTier.mutateAsync({
        plantId: downgradeModal.plant.id,
        fromTier: downgradeModal.fromTier,
        toTier: downgradeModal.toTier,
        effectiveAt: downgradeModal.effectiveAt,
      });
      setDowngradeModal(null);
      const dateStr = new Date(downgradeModal.effectiveAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      toast.success(`Downgrade scheduled for ${dateStr}.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Schedule downgrade failed.');
    }
  };

  if (!organization) {
    return (
      <div className="p-6 text-muted-foreground">
        No organization. Complete onboarding first.
      </div>
    );
  }

  if (!canSee && !isLoading) {
    return (
      <div className="p-6 text-muted-foreground">
        You don’t have permission to view billing. Contact your org owner or admin.
      </div>
    );
  }

  if (isLoading || !tierSummary || !billingPreview) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const plantsWithProperty = plants
    .map((p) => {
      const prop = properties.find((pr) => pr.id === p.property_id);
      return { ...p, property_name: prop?.name ?? '—' };
    })
    .sort((a, b) => {
      const byProp = (a.property_name ?? '').localeCompare(b.property_name ?? '');
      return byProp !== 0 ? byProp : (a.display_name ?? '').localeCompare(b.display_name ?? '');
    });

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-semibold text-foreground">Billing</h1>
        <p className="text-sm text-muted-foreground">
          Tier summary and system list. Manage payment and invoices via Stripe.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader>
            <CardTitle className="text-base">Tier summary</CardTitle>
            <CardDescription>Systems by tier</CardDescription>
          </CardHeader>
          <CardContent>
            <TierSummaryBlock summary={tierSummary} />
          </CardContent>
        </Card>
        <Card className="bg-wt-bg-secondary border-border">
          <CardHeader>
            <CardTitle className="text-base">Billing preview</CardTitle>
            <CardDescription>Estimated monthly total (before tax)</CardDescription>
          </CardHeader>
          <CardContent>
            <BillingPreviewBlock preview={billingPreview} />
          </CardContent>
        </Card>
      </div>

      <BillingEmailEditor organization={organization} />

      {nextThreshold && nextThreshold.systemsToAdd > 0 && (
        <p className="text-sm text-muted-foreground">
          Add {nextThreshold.systemsToAdd} more system{nextThreshold.systemsToAdd !== 1 ? 's' : ''} to unlock {nextThreshold.discountPct}% volume discount.
        </p>
      )}

      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Systems</CardTitle>
            <CardDescription>Per-system tier; change tier below or manage payment in Stripe.</CardDescription>
            {!organization?.stripe_customer_id && (
              <p className="text-sm text-muted-foreground mt-1">
                Billing not set up for this account. Contact support to add payment method and access invoices.
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={openStripePortal}
            disabled={portalLoading || !organization?.stripe_customer_id}
            className="gap-2"
            title={!organization?.stripe_customer_id ? 'Billing not set up. Contact support.' : undefined}
          >
            {portalLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ExternalLink className="h-4 w-4" />
            )}
            View invoices & payment
          </Button>
        </CardHeader>
        <CardContent>
          <SystemsTable
            plants={plantsWithProperty}
            onUpgrade={openUpgradeModal}
            onDowngrade={openDowngradeModal}
          />
        </CardContent>
      </Card>

      {/* Customer Billing (Stripe Connect) */}
      <ConnectSection />

      <Dialog open={!!upgradeModal} onOpenChange={(open) => !open && setUpgradeModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade tier</DialogTitle>
            <DialogDescription>
              {upgradeModal && (
                <>
                  Upgrading <strong>{upgradeModal.plant.display_name}</strong> from{' '}
                  {TIER_LABEL[upgradeModal.fromTier]} to {TIER_LABEL[upgradeModal.toTier]}.
                  <br />
                  This will add ${Math.abs(upgradeModal.deltaMonthly).toFixed(2)} to your next invoice (prorated).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmUpgrade}
              disabled={upgradeTier.isPending}
            >
              {upgradeTier.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating…
                </>
              ) : (
                'Confirm Upgrade'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!downgradeModal} onOpenChange={(open) => !open && setDowngradeModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule downgrade</DialogTitle>
            <DialogDescription>
              {downgradeModal && (
                <>
                  Downgrading <strong>{downgradeModal.plant.display_name}</strong> from{' '}
                  {TIER_LABEL[downgradeModal.fromTier]} to {TIER_LABEL[downgradeModal.toTier]}.
                  <br />
                  Effective at end of billing period (
                  {new Date(downgradeModal.effectiveAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                  ).
                  <br />
                  You keep {TIER_LABEL[downgradeModal.fromTier]} features until then. No refund.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDowngradeModal(null)}>
              Cancel
            </Button>
            <Button
              variant="secondary"
              onClick={handleConfirmDowngrade}
              disabled={downgradeTier.isPending}
            >
              {downgradeTier.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scheduling…
                </>
              ) : (
                'Schedule Downgrade'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TierSummaryBlock({ summary }: { summary: TierSummary }) {
  return (
    <div className="space-y-2 text-sm">
      <p>
        <span className="text-muted-foreground">Total systems:</span>{' '}
        <span className="font-medium">{summary.total_systems}</span>
      </p>
      <div className="flex flex-wrap gap-2">
        {(['monitor', 'alert', 'predict'] as const).map((t) => (
          <span key={t} className="text-muted-foreground">
            {TIER_LABEL[t]}: <span className="font-medium text-foreground">{summary.tiers[t]}</span>
          </span>
        ))}
      </div>
      <p className="text-muted-foreground">
        Highest tier: <span className="font-medium text-foreground">{TIER_LABEL[summary.highest_tier] ?? summary.highest_tier}</span>
      </p>
    </div>
  );
}

function BillingPreviewBlock({
  preview,
}: {
  preview: BillingPreview;
  nextThreshold?: { nextThreshold: number; discountPct: number; systemsToAdd: number } | null;
}) {
  return (
    <div className="space-y-2 text-sm">
      <p>
        <span className="text-muted-foreground">Monthly total:</span>{' '}
        <span className="font-medium">${preview.monthly_total.toFixed(2)}</span>
      </p>
      {preview.volume_discount_pct != null && preview.volume_discount_pct > 0 && (
        <p className="text-muted-foreground">
          Volume discount: {preview.volume_discount_pct}%
        </p>
      )}
      <p className="text-muted-foreground">
        Effective per system: ${preview.effective_price_per_system.toFixed(2)}/mo
      </p>
    </div>
  );
}

function SystemsTable({
  plants,
  onUpgrade,
  onDowngrade,
}: {
  plants: PlantRow[];
  onUpgrade: (plant: PlantRow, fromTier: AccountTier, toTier: AccountTier) => void;
  onDowngrade: (plant: PlantRow, fromTier: AccountTier, toTier: AccountTier) => void;
}) {
  if (plants.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No systems. Add properties and systems to see tier breakdown.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Property</TableHead>
          <TableHead>System</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead className="w-[80px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {plants.map((p) => {
          const tier = (p.tier ?? 'monitor') as AccountTier;
          return (
            <TableRow key={p.id}>
              <TableCell className="font-medium">{p.property_name}</TableCell>
              <TableCell>{p.display_name}</TableCell>
              <TableCell>
                <Badge variant="secondary" className="font-normal">
                  {TIER_LABEL[tier] ?? tier}
                </Badge>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Tier actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {tier === 'monitor' && (
                      <>
                        <DropdownMenuItem onClick={() => onUpgrade(p, 'monitor', 'alert')}>
                          Upgrade to Alert
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onUpgrade(p, 'monitor', 'predict')}>
                          Upgrade to Predict
                        </DropdownMenuItem>
                      </>
                    )}
                    {tier === 'alert' && (
                      <>
                        <DropdownMenuItem onClick={() => onDowngrade(p, 'alert', 'monitor')}>
                          Downgrade to Monitor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onUpgrade(p, 'alert', 'predict')}>
                          Upgrade to Predict
                        </DropdownMenuItem>
                      </>
                    )}
                    {tier === 'predict' && (
                      <>
                        <DropdownMenuItem onClick={() => onDowngrade(p, 'predict', 'alert')}>
                          Downgrade to Alert
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDowngrade(p, 'predict', 'monitor')}>
                          Downgrade to Monitor
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function BillingEmailEditor({ organization }: { organization: { id: string; billing_email?: string | null } }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(organization.billing_email ?? '');
  const [saving, setSaving] = useState(false);
  const { refreshData } = useApp();

  useEffect(() => {
    setValue(organization.billing_email ?? '');
  }, [organization.billing_email]);

  const handleSave = async () => {
    const trimmed = value.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ billing_email: trimmed || null })
        .eq('id', organization.id);
      if (error) throw error;
      setEditing(false);
      toast.success('Billing email updated.');
      void refreshData();
    } catch {
      toast.error('Failed to update billing email.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(organization.billing_email ?? '');
    setEditing(false);
  };

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader>
        <CardTitle className="text-base">Billing contact</CardTitle>
        <CardDescription>Invoices and billing notifications are sent to this email.</CardDescription>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label htmlFor="billing-email" className="text-sm text-muted-foreground">Email</Label>
              <Input
                id="billing-email"
                type="email"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="billing@example.com"
                className="mt-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSave();
                  if (e.key === 'Escape') handleCancel();
                }}
              />
            </div>
            <Button size="icon" variant="ghost" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
            <Button size="icon" variant="ghost" onClick={handleCancel} disabled={saving}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <span className={organization.billing_email ? 'text-foreground' : 'text-muted-foreground'}>
              {organization.billing_email || 'No billing email set'}
            </span>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditing(true)}>
              <Pencil className="h-3 w-3" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const PLATFORM_FEE_LABELS: Record<string, { tier: string; fee: string }> = {
  monitor: { tier: 'Monitor', fee: '$4' },
  alert: { tier: 'Alert', fee: '$7' },
  predict: { tier: 'Predict', fee: '$15' },
};

function ConnectSection() {
  const { connectStatus, startOnboarding, isLoading, error } = useConnectAccount();
  const { organization } = useApp();

  if (connectStatus === 'not_connected') {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-base">Customer Billing</CardTitle>
          <CardDescription>
            Bill your customers directly for monitoring. You set the price; Watt Tower's platform fee is automatically deducted.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border p-3 space-y-2 text-sm">
            <p className="text-muted-foreground font-medium">Platform fee per system/month:</p>
            {Object.entries(PLATFORM_FEE_LABELS).map(([key, { tier, fee }]) => (
              <div key={key} className="flex justify-between text-muted-foreground">
                <span>{tier}</span>
                <span className="font-mono">{fee}/mo</span>
              </div>
            ))}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button onClick={startOnboarding} disabled={isLoading} className="gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            Set Up Customer Payments
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (connectStatus === 'onboarding_pending') {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-base">Customer Billing</CardTitle>
          <CardDescription>
            Finish setting up your payment account to start billing customers.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-destructive mb-3">{error}</p>}
          <Button onClick={startOnboarding} disabled={isLoading} className="gap-2">
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="h-4 w-4" />
            )}
            Continue Setup
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Active
  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Customer Billing</CardTitle>
          <CardDescription>
            Charge your customers directly. Manage payouts via Stripe.
          </CardDescription>
        </div>
        <Badge variant="outline" className="bg-wt-green-primary/10 text-wt-green-primary border-wt-green-primary/20">
          Active
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-border p-3 space-y-2 text-sm">
          <p className="text-muted-foreground font-medium">Platform fee per system/month:</p>
          {Object.entries(PLATFORM_FEE_LABELS).map(([key, { tier, fee }]) => (
            <div key={key} className="flex justify-between text-muted-foreground">
              <span>{tier}</span>
              <span className="font-mono">{fee}/mo</span>
            </div>
          ))}
        </div>
        <p className="text-sm text-muted-foreground">
          To bill a customer, go to any property and select "Start billing this customer."
        </p>
        <ConnectDashboardButton connectAccountId={organization?.stripe_connect_account_id ?? null} />
      </CardContent>
    </Card>
  );
}

function ConnectDashboardButton({ connectAccountId }: { connectAccountId: string | null }) {
  const [loading, setLoading] = useState(false);

  const openDashboard = async () => {
    if (!connectAccountId) return;
    setLoading(true);
    try {
      const data = await invokeAuthedFunction<{ url?: string; error?: string }>(
        supabase,
        'create-connect-login-link',
        { connect_account_id: connectAccountId }
      );
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        // Fallback: direct to Stripe Express dashboard
        window.open('https://connect.stripe.com/express_login', '_blank');
      }
    } catch {
      // Fallback
      window.open('https://connect.stripe.com/express_login', '_blank');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={openDashboard} disabled={loading} className="gap-2">
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
      View Stripe Dashboard
    </Button>
  );
}
